import {useEffect, useState} from "react";
import {API_BASE_URL} from "../config/api";

export interface DestinationPhoto {
  id: string;
  url: string;
  photographer: string;
  profile_url: string;
  photo_url: string;
}
type ImageResult = {src: string; photo?: DestinationPhoto};
export type ImageVariant = "card" | "list_short" | "long";
const probes = new Map<string, Promise<boolean>>();
const photos = new Map<string, Promise<DestinationPhoto | undefined>>();

function imageExists(src: string): Promise<boolean> {
  if (!probes.has(src)) {
    probes.set(src, new Promise(resolve => {
      const img = new Image();
      const timer = setTimeout(() => resolve(false), 8000);
      img.onload = () => { clearTimeout(timer); resolve(true); };
      img.onerror = () => { clearTimeout(timer); resolve(false); };
      img.src = src;
    }));
  }
  return probes.get(src)!;
}

async function getPhoto(iata: string): Promise<DestinationPhoto | undefined> {
  if (!photos.has(iata)) {
    const request = (async () => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(`${API_BASE_URL}/destination-images/${iata}`, {signal: controller.signal});
        if (!response.ok) return undefined;
        return (await response.json()).photo || undefined;
      } catch { return undefined; }
      finally { clearTimeout(timer); }
    })();
    photos.set(iata, request);
    // Retry temporary failures on subsequent mounts after a short cooldown.
    request.then(photo => { if (!photo) setTimeout(() => photos.delete(iata), 60000); });
  }
  return photos.get(iata)!;
}

export function resizedPhotoUrl(raw: string, variant: ImageVariant): string {
  const sizes = {card: [480, 640], list_short: [528, 174], long: [1920, 400]};
  const [width, height] = sizes[variant];
  const url = new URL(raw);
  url.searchParams.set("w", String(width));
  url.searchParams.set("h", String(height));
  url.searchParams.set("fit", "crop");
  url.searchParams.set("auto", "format");
  url.searchParams.set("q", "80");
  return url.toString();
}

export async function resolveDestinationImage(iata: string, variant: ImageVariant, preferred?: string): Promise<ImageResult> {
  const code = iata.trim().toUpperCase();
  const fallback = `/images/destinations/${variant}/default.png`;
  if (!/^[A-Z]{3}$/.test(code)) return {src: fallback};
  const variants: ImageVariant[] = [variant, ...(["card", "list_short", "long"] as ImageVariant[]).filter(v => v !== variant)];
  const candidates = Array.from(new Set([
    ...(preferred && !preferred.includes("/default.") ? [preferred] : []),
    ...variants.map(v => `/images/destinations/${v}/${code}.png`),
    `/images/destinations/${code}.png`,
  ]));
  for (const src of candidates) {
    if (await imageExists(src)) return {src};
  }
  const photo = await getPhoto(code);
  if (photo) {
    try {
      const src = resizedPhotoUrl(photo.url, variant);
      if (await imageExists(src)) return {src, photo};
    } catch { /* Invalid provider URL: keep the local fallback. */ }
  }
  return {src: fallback};
}

export function useDestinationImage(iata: string, variant: ImageVariant, preferred?: string): ImageResult {
  const fallback = `/images/destinations/${variant}/default.png`;
  const identity = `${iata}|${variant}|${preferred || ""}`;
  const [state, setState] = useState<{identity: string; image: ImageResult}>();
  useEffect(() => {
    let active = true;
    resolveDestinationImage(iata, variant, preferred).then(image => {
      if (active) setState({identity, image});
    });
    return () => { active = false; };
  }, [iata, variant, preferred, identity]);
  return state?.identity === identity ? state.image : {src: fallback};
}
